/**
 * The floor over the one in view, drawn while the walk is under a stairwell into it, as the app shows it: the level above
 * the viewed one comes into 3D while a ceiling of the viewed one has an opening and the camera is under that ceiling
 * (LevelMaterialManager.CheckGlassCeiling), and a climb keeps it in view to the switch (EditorHelper.StairRevealLevelIndex),
 * «so the camera does not come up into a black void». Without it the walk looks up a stairwell into the sky.
 *
 * It is context, like the floors below (`LowerFloors`): nothing of it is picked or edited, and it stands at the difference
 * of the two floors' altitudes over the floor in view. Its shell is all of it — walls, floors with the stairwells' holes,
 * ceilings — as one mesh per finish; of its furniture, doors and windows only what stands in or round the rooms the
 * stairwells open into, the only ones the walk sees from below, loads, after the floor in view has loaded its own.
 */
import * as THREE from 'three';
import type { Level, Scene as HPScene, Vec2 } from '../../core/hp/types';
import { buildWallSolids } from '../../core/geometry/walls';
import { wellRoom, wellsKey, wellsUp } from '../../core/geometry/stairwells';
import { containsPoint } from '../../state/sceneGesture';
import { levelAltitude } from '../../state/floors';
import { buildScene, isRegion, placeholderFor } from './buildScene';
import { HydrationQueue, type HydrationTask } from './HydrationQueue';
import { buildOpeningModel, type ModelLibrary } from './ModelLibrary';
import { disposeTree, edgeDistance, freeze, LOWER_FLOORS_ORDER, mergeByMaterial, roomAt, type LowerFloorsOptions } from './LowerFloors';

/** How far from a stairwell room's outline a door or a window still belongs to it: the thickness of the wall it is in. */
const WALL_REACH = .4;

export class FloorAbove {
  readonly root = new THREE.Group();
  private shell: { key: string; root: THREE.Group; release(): void } | null = null;
  private queue: HydrationQueue<THREE.Object3D>;
  private nodes = new Map<string, THREE.Object3D>();
  private releases = new WeakMap<THREE.Object3D, () => void>();
  private inputs: { level: Level | null; wells: string; textures: number; offset: number } | null = null;
  private disposed = false;
  private readonly aniso: number;

  constructor(private readonly options: LowerFloorsOptions) {
    this.root.name = 'floor-above';
    this.root.userData.noContactShadows = true;
    this.root.matrixAutoUpdate = false; this.root.renderOrder = LOWER_FLOORS_ORDER;
    this.aniso = options.maxAnisotropy();
    this.queue = new HydrationQueue<THREE.Object3D>(node => this.release(node), 4, 120_000, () => options.admit?.() ?? true);
    this.queue.onIdle = () => { if (!this.disposed) options.settled?.(); };
  }

  /** The floor that is drawn, for checks: its index and how much of it is built. */
  describe(): { level: string | null; offset: number; items: number; openings: number } {
    let items = 0, openings = 0;
    for (const key of this.nodes.keys()) if (key.startsWith('above:item:')) items++; else openings++;
    return { level: this.inputs?.level?.name ?? null, offset: this.inputs?.offset ?? 0, items, openings };
  }

  resume(): void { this.queue.resume(); }

  /**
   * The floor over `index` while `shown` — the walk is on — and one of that floor's own flights leads up into it;
   * nothing otherwise. False when nothing changed.
   */
  update(hp: HPScene | null, index: number, textures: number, shown: boolean): boolean {
    if (this.disposed) return false;
    const levels = hp?.levels ?? [], wells = wellsUp(levels, index), level = shown && wells.length ? levels[index + 1] ?? null : null;
    const key = wellsKey(wells), offset = level ? levelAltitude(levels, index + 1) - levelAltitude(levels, index) : 0;
    const previous = this.inputs;
    if (previous && previous.level === level && previous.wells === key && previous.textures === textures && previous.offset === offset) return false;
    this.inputs = { level, wells: key, textures, offset };
    this.root.position.y = offset; this.root.updateMatrix(); this.root.visible = !!level;
    const shellKey = level ? JSON.stringify([key, textures]) : '';
    if (this.shell && (!level || this.shell.key !== shellKey || previous?.level?.walls !== level.walls
      || previous?.level?.rooms !== level.rooms || previous?.level?.openings !== level.openings)) {
      this.shell.root.removeFromParent(); this.shell.release(); disposeTree(this.shell.root); this.shell = null;
    }
    if (level && hp && !this.shell) this.shell = this.buildShell(hp, levels, index, level, shellKey);
    this.reconcile(level, level ? wells.map(well => wellRoom(level.rooms, well)?.index).filter((room): room is number => room !== undefined) : []);
    this.root.updateMatrixWorld(true);
    return true;
  }

  dispose(): void {
    this.disposed = true;
    this.queue.dispose();
    if (this.shell) { this.shell.root.removeFromParent(); this.shell.release(); disposeTree(this.shell.root); this.shell = null; }
    this.nodes.clear();
    this.root.removeFromParent();
  }

  /** Walls, floors with the holes over the flights below, ceilings with their own: seen from under them, the floor's holes need no edge. */
  private buildShell(hp: HPScene, levels: readonly Level[], index: number, level: Level, key: string): { key: string; root: THREE.Group; release(): void } {
    const built = buildScene({ ...hp, levels: [{ ...level, items: [] }] }, {
      maxAnisotropy: this.aniso, showCeiling: true, textureImages: this.options.models.textureImages,
      wells: { floor: wellsUp(levels, index), ceiling: wellsUp(levels, index + 1), floorEdges: false },
    });
    built.root.getObjectByName('items')?.removeFromParent();
    for (const wall of level.walls) { const node = built.root.getObjectByName(`wall:${wall.id}`); if (node) node.visible = !wall.hidden; }
    const root = mergeByMaterial(built.root);
    root.name = 'floor-above-shell';
    freeze(root);
    this.root.add(root);
    return { key, root, release: this.options.retain(root) };
  }

  /** The furniture of the rooms the stairwells open into, and the doors and windows round them. */
  private reconcile(level: Level | null, rooms: number[]): void {
    const tasks: HydrationTask<THREE.Object3D>[] = [], models = this.options.models, aniso = this.aniso;
    if (level) {
      const open = new Set(rooms), outlines = level.rooms.filter(room => open.has(room.index)).map(room => room.polygon);
      for (const item of level.items) {
        if (!open.has(roomAt(level.rooms, item.position)?.index ?? -1)) continue;
        const key = `above:item:${item.id}`, procedural = isRegion(item);
        tasks.push({ key, signature: JSON.stringify(item), revision: item, kind: procedural ? 'procedural' : 'item', label: item.caption ?? item.modelRef.id,
          build: async () => {
            if (!procedural) return models.instance(item, aniso);
            const panel = placeholderFor(item, aniso, models.textureImages);
            panel.userData.generated = true; this.releases.set(panel, this.options.retain(panel));
            return panel;
          },
          apply: node => this.place(key, node) });
      }
      const near = (point: Vec2) => outlines.some(outline => containsPoint(outline, point) || edgeDistance(outline, point) <= WALL_REACH);
      const openings = new Map(level.openings.map(opening => [opening.id, opening]));
      for (const solid of buildWallSolids(level)) for (const reveal of solid.reveals) {
        const opening = openings.get(reveal.openingId), wall = solid.wall;
        if (!opening || wall.hidden || !near({ x: wall.start.x + wall.dir.x * opening.offset, z: wall.start.z + wall.dir.z * opening.offset })) continue;
        const key = `above:opening:${opening.id}`;
        tasks.push({ key, signature: JSON.stringify([opening, reveal.frame]), revision: opening, kind: 'opening', label: opening.caption ?? opening.modelRef.id,
          build: () => buildOpeningModel(models as ModelLibrary, opening, reveal.frame, aniso), apply: node => this.place(key, node) });
      }
    }
    this.queue.reconcile(tasks);
  }

  private place(key: string, node: THREE.Object3D): void {
    if (this.disposed || !this.inputs?.level) return;
    this.nodes.set(key, node);
    node.userData.floorAboveKey = key;
    this.root.add(node); freeze(node);
    this.root.updateMatrixWorld(true);
    this.options.changed?.();
  }

  private release(node: THREE.Object3D): void {
    const key = node.userData.floorAboveKey as string | undefined;
    if (key && this.nodes.get(key) === node) this.nodes.delete(key);
    const release = this.releases.get(node);
    if (release) { this.releases.delete(node); release(); }
    if (node.userData.generated) { node.removeFromParent(); disposeTree(node); }
    else this.options.models.release(node);
    if (!this.disposed) this.options.changed?.();
  }
}
